import { Fragment } from 'react';
import { User } from 'components/common';
import { defaultBlockData } from '../editor/blocks';
import {
  FillInAnswer,
  H5PAnswer,
  LikertAnswer,
  MultipleChoiceAnswer,
  NumericQuestionAnswer,
  OpenQuestionAnswer,
  Reviews,
  ScormAnswer,
  SequencingAnswer,
  TaskQuestion
} from 'components/common/tasks';
import { useReduxState } from 'hooks';

import type { GetUserCourseAnswersDataRes } from 'shared-types/responses';

interface Props {
  data: GetUserCourseAnswersDataRes;
}

const ProfileAnswers = ({ data }: Props) => {
  const { messages, serverConfig, theme } = useReduxState();

  if (!data) return messages.commonError;

  const { course, includeReviews, tasks, tasksData, tasksOrder, user } = data;

  const answers = tasksData ? tasksData.answers : null;
  const answersOrder = tasksOrder
    ? tasksOrder.filter((taskId) => tasksData?.answers[taskId] !== undefined)
    : null;

  return (
    <div className="wrapper">
      {serverConfig.courseAnswersSummaryContent ? (
        <div
          className="custom-content"
          dangerouslySetInnerHTML={{
            __html: serverConfig.courseAnswersSummaryContent
          }}
        />
      ) : null}
      <div className="user">
        <User user={user} />
      </div>

      <h1 className="course-title">{course.title}</h1>

      {answersOrder && answersOrder.length ? (
        <div className="answers">
          {answersOrder.map((taskId, i) => {
            const task = tasks[taskId];
            const answer = answers[taskId];
            const maxScore = task.data.hasOwnProperty('pointsWeight')
              ? task.data.pointsWeight
              : defaultBlockData[task.type].pointsWeight;
            const reviews = includeReviews ? answer.reviews : [];

            return (
              <Fragment key={taskId}>
                {i === 0 ||
                task.chapter.number !==
                  tasks[answersOrder[i - 1]].chapter.number ? (
                  <div className="chapter-title">
                    {`${task.chapter.number}. ${task.chapter.title}`}
                  </div>
                ) : null}

                <div className="answer">
                  <TaskQuestion
                    question={
                      task.type === 'likert'
                        ? task.data.description
                        : task.data.question
                    }
                  />

                  {task.type === 'multiplechoice' ? (
                    <MultipleChoiceAnswer
                      answers={answer.data.answers}
                      maxScore={maxScore}
                      options={task.data.options}
                      score={answer.score}
                    />
                  ) : task.type === 'numericquestion' ? (
                    <NumericQuestionAnswer
                      answer={answer.data.answer}
                      maxScore={maxScore}
                      score={answer.score}
                    />
                  ) : task.type === 'openquestion' ? (
                    <OpenQuestionAnswer
                      filename={answer.data.filename}
                      filepath={answer.data.filepath}
                      maxScore={maxScore}
                      score={answer.score}
                      text={answer.data.text}
                    />
                  ) : task.type === 'h5p' ? (
                    <H5PAnswer
                      answers={answer.data.userAnswers || {}}
                      completion={answer.data.completion}
                      maxScore={maxScore}
                      missingCorrectAnswers={answer.data.missingCorrectAnswers}
                      success={answer.data.success}
                      score={answer.data.score}
                      points={answer.score as number}
                      task={task}
                      showPreview={true}
                    />
                  ) : task.type === 'scorm' ? (
                    <ScormAnswer
                      completion={answer.data.completion}
                      maxScore={maxScore}
                      success={answer.data.success}
                      score={answer.data.score}
                      points={answer.score}
                      task={task}
                      taskId={taskId}
                      showPreview={true}
                    />
                  ) : task.type === 'fillin' ? (
                    <FillInAnswer
                      answers={answer.data.answers}
                      contentData={task.data.contentData}
                      correctAnswers={answer.correctAnswers}
                      maxScore={maxScore}
                      score={answer.score}
                    />
                  ) : task.type === 'likert' ? (
                    <LikertAnswer
                      answers={answer.data.answers}
                      questions={task.data.questions}
                      options={task.data.options}
                    />
                  ) : task.type === 'sequencing' ? (
                    <SequencingAnswer
                      answers={answer.data.answers}
                      maxScore={maxScore}
                      options={task.data.options}
                      score={answer.score}
                    />
                  ) : null}

                  {task.type === 'openquestion' && reviews.length ? (
                    <Reviews
                      answerId={answer.id}
                      answerUser={user}
                      courseId={course.id}
                      gradingType={task.data.gradingType}
                      maxPoints={maxScore}
                      onlyComments={task.data.grading === 'none'}
                      isAdmin={false}
                      reviews={reviews}
                    />
                  ) : null}
                </div>
              </Fragment>
            );
          })}
        </div>
      ) : (
        <div className="empty">{messages.tasksNoAnswers}</div>
      )}

      <style jsx>{`
        .wrapper {
          color: ${theme.textColor};
          padding: 15px;
          margin: 0 auto;
          max-width: 100%;
          width: 720px;
        }
        .custom-content {
          margin-bottom: 50px;
          max-width: 100%;
          overflow: hidden;
        }
        .user {
          margin-bottom: 15px;
          text-align: right;
        }
        .course-title {
          font-size: 2.4rem;
          margin: 0 0 40px;
        }
        .chapter-title {
          font-size: 2rem;
          font-weight: 700;
          margin: 40px 0 20px;
        }
        .answer {
          background-color: ${theme.lightGrey};
          break-inside: avoid;
          border-radius: ${theme.cardBorderRadius};
          margin-top: 25px;
          padding: 15px;
        }
        .empty {
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default ProfileAnswers;
